import { SpeakerFaceCoordinatesResponse } from '@goldcast/api/content';
import { v4 as uuidv4 } from 'uuid';

import { COMPOSITION_DIMENSIONS, VIDEO_QUALITY } from '../constants/Remotion.constants';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';
import { CompositeSegment } from '../types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { createIntroVideoSegment } from './videoSegment/introVideoSegment';
import { createOutroVideoSegment } from './videoSegment/outroVideoSegment';
import { MAIN_TRACK_ID } from '../constants/player';
import { mapEffectsToPlayableTimeline } from './animation/effectUtilts';
import {
  createCompositeFromOldComposite,
  createMainCompositeSegment,
  createMainCompositeSegmentWithMetaData
} from './compositeSegment/compositeSegment';
import { getSegmentTranslatedCaptions } from './compositeSegment/utils';
import { injectSegmentMetaDataInCompositeSegment, injectUpdatedMainTrackInPlayerConfig } from './playerConfigHelpers';
import { updateVideoSegmentWithMetaData } from './videoSegment/videoSegmentWithMetadata';
import { selectSpeakerAnalysis, selectVideoFeeds } from '../selectors/contentMetadataStoreSelectors';
import { BrandKitStore, PlatformBrandKitStore } from '../types/brand-kit-types';
import { Clip } from '../types/clip-types';
import { CompositionConfig, SelectionElementType } from '../types/RemotionPlayerConfigTypes/CompositionConfig';
import { ElementsTrack } from '../types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { SegmentsTrack } from '../types/RemotionPlayerConfigTypes/Track/SegmentsTrack';
import { Track } from '../types/RemotionPlayerConfigTypes/Track/Track';
import { SceneChanges } from '../types/scene-changes';
import { SegmentType } from '../types/Segment/Type';
import { TrackType } from '../types/Track/Type';
import { buildCaptionMetadata } from '../utils/captionUtils';
import {
  getAllSegments,
  getClipFPS,
  getClipReadOnlyPlayerConfig,
  isClipAudiogram,
  isFullLengthClip,
  isNotFullLengthClip,
  isSplitsInTimelineEnabledPure
} from '../utils/clipUtils';
import { updatePlayerConfigWithDeletes } from '../utils/elementUtils';
import { getFontObject } from '../utils/fontUtils';
import { getPlayerConfigElements } from '../utils/playerConfigGetters';
import { getPlayerConfigMetadata } from '../utils/playerConfigUtils';
import { mapScenesToPlayableTimeline } from '../utils/sceneUtils';

const getMaxBoundsForCompositeSegment = (clip: Clip) => {
  const fps = getClipFPS(clip);
  let maxBounds = {
    fromFrame: 0,
    toFrame: clip.asset_metadata.end * fps
  };

  if (isNotFullLengthClip(clip) && typeof clip.asset_metadata.maxVideoDuration === 'number') {
    maxBounds = {
      fromFrame: 0,
      toFrame: clip.asset_metadata.maxVideoDuration * fps
    };
  }

  if (
    typeof clip.asset_metadata.maxVideoDuration === 'number' &&
    clip.asset_metadata.maxVideoDuration > clip.asset_metadata.end &&
    isFullLengthClip(clip)
  ) {
    maxBounds = {
      fromFrame: 0,
      toFrame: clip.asset_metadata.maxVideoDuration * fps
    };
  }
  return maxBounds;
};

export function getPlayerConfigWithSplits({
  clip,
  sceneOverrides,
  shouldUpdatePlayerConfig,
  facialRecognitionToDefaultPosition,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled,
  isScreenshareDetectionEnabled,
  isPlatformBrandKitEnabled,
  clipBrandKitStore,
  brandKitStore,
  platformBrandKit,
  organization,
  isSplitsTranslationEnabled,
  zoomLevel,
  previousClip, // currentClip.getSnapshot()[clip.id]
  contentMetadataStoreInstance
}: {
  previousClip: Clip;
  clip: Clip;
  sceneOverrides?: SceneChanges;
  shouldUpdatePlayerConfig?: boolean;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
  isScreenshareDetectionEnabled: boolean;
  isPlatformBrandKitEnabled: boolean;
  clipBrandKitStore: BrandKitStore;
  brandKitStore: BrandKitStore;
  platformBrandKit: PlatformBrandKitStore;
  organization?: string | null | undefined;
  isSplitsTranslationEnabled: boolean;
  zoomLevel: number;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
}): CompositionConfig {
  if (!shouldUpdatePlayerConfig) {
    return clip.asset_metadata.player_config as CompositionConfig;
  }

  const contentId = clip.content.id;
  const storeState = contentMetadataStoreInstance.getState();
  const videoFeeds = selectVideoFeeds(contentId)(storeState);
  const speakerAnalysis = selectSpeakerAnalysis(contentId)(storeState);
  const alignedRecordings = storeState.data[contentId]?.metadata?.alignedRecordings;

  const videoQuality = VIDEO_QUALITY.FULL_HD;
  const height = COMPOSITION_DIMENSIONS[clip.asset_metadata.size][videoQuality].HEIGHT;
  const width = COMPOSITION_DIMENSIONS[clip.asset_metadata.size][videoQuality].WIDTH;

  const fps = getClipFPS(clip);

  const showCaptions = !!clip.asset_metadata.subtitle || isClipAudiogram(clip);

  const segmentsTrack: SegmentsTrack = {
    id: MAIN_TRACK_ID,
    name: 'Segments Track',
    type: TrackType.SEGMENTS,
    hideTrack: {
      inTimeline: false,
      inPlayer: false
    },
    segments: []
  };
  const existingSegmentsTrack = clip.asset_metadata.player_config?.playerConfig.tracks.find(
    track => track.type === TrackType.SEGMENTS
  ) as SegmentsTrack | undefined;
  segmentsTrack.segmentTransitions = existingSegmentsTrack?.segmentTransitions || [];
  const clipWithSegmentMetaData = injectSegmentMetaDataInCompositeSegment(clip);

  let durationInFrames = 0;

  if (clipWithSegmentMetaData.asset_metadata.player_config) {
    const allSegments = getAllSegments(clipWithSegmentMetaData);
    for (const segment of allSegments) {
      const isCompositeSegment = segment.type === SegmentType.COMPOSITE;
      if (isCompositeSegment) {
        const compositeSegmentDuration = (segment.segment_metadata?.duration || 0) * fps;
        const newCompositeSegment = createCompositeFromOldComposite({
          clip: clipWithSegmentMetaData,
          fps,
          duration: compositeSegmentDuration,
          offset: durationInFrames,
          sceneOverrides,
          facialRecognitionToDefaultPosition,
          oldCompositeSegment: segment,
          videoFeeds,
          speakerAnalysis,
          alignedRecordings,
          isFullFaceRecEnabled,
          isFullFaceRecForPreRecordedEnabled,
          isScreenshareDetectionEnabled,
          isPlatformBrandKitEnabled,
          clipBrandKitStore,
          brandKitStore,
          platformBrandKit,
          organization,
          isSplitsTranslationEnabled
        });

        if (segment.effects) {
          newCompositeSegment.effects = segment.effects;
        }

        const playbackAdjustedEffects = mapEffectsToPlayableTimeline(
          newCompositeSegment.effects,
          newCompositeSegment.playRanges,
          fps
        );

        if (
          newCompositeSegment.segment_metadata &&
          clip.asset_metadata.selected_lang &&
          newCompositeSegment.segment_metadata.captions_lang !== clip.asset_metadata.selected_lang &&
          isSplitsTranslationEnabled
        ) {
          newCompositeSegment.segment_metadata.captions = getSegmentTranslatedCaptions(newCompositeSegment, clip);
          newCompositeSegment.segment_metadata.captions_lang = clip.asset_metadata.selected_lang;
        }

        const playbackAdjustedScenes = mapScenesToPlayableTimeline(
          newCompositeSegment.scenes,
          newCompositeSegment.playRanges
        );

        const maxBounds = segment.maxBounds;

        segmentsTrack.segments.push({
          ...newCompositeSegment,
          playbackAdjustedEffects,
          playbackAdjustedScenes,
          maxBounds
        });
        durationInFrames += compositeSegmentDuration;
      } else {
        if (segment.type === SegmentType.VIDEO) {
          const newSegment = updateVideoSegmentWithMetaData(clip, segment, durationInFrames);
          durationInFrames += newSegment.toFrame - newSegment.fromFrame;
          segmentsTrack.segments.push(newSegment);
        } else {
          throw new Error('Type not implemented');
        }
      }
    }
  } else {
    const compositeSegmentDuration = (clip.asset_metadata?.duration || 0) * fps;
    const maxBounds = getMaxBoundsForCompositeSegment(clip);
    const compositeSegment = createMainCompositeSegmentWithMetaData({
      clip,
      fps,
      duration: compositeSegmentDuration,
      offset: durationInFrames,
      sceneOverrides,
      facialRecognitionToDefaultPosition,
      videoFeeds,
      speakerAnalysis,
      alignedRecordings,
      isFullFaceRecEnabled,
      isFullFaceRecForPreRecordedEnabled,
      isScreenshareDetectionEnabled,
      isPlatformBrandKitEnabled,
      clipBrandKitStore,
      brandKitStore,
      platformBrandKit,
      organization
    });

    const playbackAdjustedEffects = mapEffectsToPlayableTimeline(
      compositeSegment.effects,
      compositeSegment.playRanges,
      fps
    );

    const playbackAdjustedScenes = mapScenesToPlayableTimeline(compositeSegment.scenes, compositeSegment.playRanges);

    segmentsTrack.segments.push({
      ...compositeSegment,
      playbackAdjustedEffects,
      playbackAdjustedScenes,
      maxBounds
    });
    durationInFrames += compositeSegmentDuration;
  }

  injectUpdatedMainTrackInPlayerConfig(clipWithSegmentMetaData, segmentsTrack);

  let tracks: Track[] = [];
  if (segmentsTrack.segments.length) {
    const fontObject = getFontObject({
      clip: clipWithSegmentMetaData,
      fontTarget: SelectionElementType.CAPTIONS,
      clipBrandKitStore,
      brandKitStore,
      platformBrandKit,
      organization,
      isPlatformBrandKitEnabled
    });
    const existingElementsTracks = getPlayerConfigElements(clipWithSegmentMetaData?.asset_metadata?.player_config);

    let updatedElementsTracks: ElementsTrack[] = [];
    if (existingElementsTracks) {
      updatedElementsTracks = updatePlayerConfigWithDeletes({
        playerConfig: existingElementsTracks,
        fps,
        prevClip: previousClip,
        newClip: clipWithSegmentMetaData,
        isSplitsTranslationEnabled,
        zoomLevel
      });
    }
    const metadata = buildCaptionMetadata({
      clip: clipWithSegmentMetaData,
      mainTrack: segmentsTrack,
      isSplitsTranslationEnabled,
      clipBrandKitStore,
      brandKitStore,
      platformBrandKit,
      organization
    });

    tracks.push({
      segmentId: 'random',
      name: 'Captions',
      id: uuidv4(),
      type: TrackType.CAPTIONS,
      hideTrack: {
        inPlayer: !showCaptions,
        inTimeline: true
      },
      pages: [],
      metadata,
      fromFrame: 0,
      toFrame: durationInFrames,
      ...fontObject
    });

    tracks.unshift(...updatedElementsTracks);
    tracks.unshift(segmentsTrack);
  } else {
    tracks = clip.asset_metadata.player_config?.playerConfig?.tracks || [];
  }

  return {
    height,
    width,
    fps,
    durationInFrames: Math.floor(durationInFrames),
    version: '0.0.1',
    playerConfig: {
      tracks,
      metadata: getPlayerConfigMetadata(getClipReadOnlyPlayerConfig(clip))
    }
  };
}
export function getPlayerConfig({
  clip,
  sceneOverrides,
  shouldUpdatePlayerConfig,
  facialRecognitionToDefaultPosition,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled,
  isScreenshareDetectionEnabled,
  isPlatformBrandKitEnabled,
  clipBrandKitStore,
  brandKitStore,
  platformBrandKit,
  organization,
  isSplitsTranslationEnabled,
  zoomLevel,
  previousClip,
  contentMetadataStoreInstance
}: {
  clip: Clip;
  sceneOverrides?: SceneChanges;
  shouldUpdatePlayerConfig?: boolean;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
  isScreenshareDetectionEnabled: boolean;
  isPlatformBrandKitEnabled: boolean;
  clipBrandKitStore: BrandKitStore;
  brandKitStore: BrandKitStore;
  platformBrandKit: PlatformBrandKitStore;
  organization?: string | null | undefined;
  isSplitsTranslationEnabled: boolean;
  zoomLevel: number;
  previousClip: Clip;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
}): CompositionConfig {
  if (!shouldUpdatePlayerConfig) {
    return clip.asset_metadata.player_config as CompositionConfig;
  }

  const isSplitsEnabled = isSplitsInTimelineEnabledPure({ clip, isSplitsTranslationEnabled });
  if (isSplitsEnabled) {
    return getPlayerConfigWithSplits({
      clip,
      sceneOverrides,
      shouldUpdatePlayerConfig,
      facialRecognitionToDefaultPosition,
      isFullFaceRecEnabled,
      isFullFaceRecForPreRecordedEnabled,
      isScreenshareDetectionEnabled,
      isPlatformBrandKitEnabled,
      clipBrandKitStore,
      brandKitStore,
      platformBrandKit,
      organization,
      isSplitsTranslationEnabled,
      zoomLevel,
      previousClip,
      contentMetadataStoreInstance
    });
  }

  const contentId = clip.content.id;
  const storeState = contentMetadataStoreInstance.getState();
  const videoFeeds = selectVideoFeeds(contentId)(storeState);
  const speakerAnalysis = selectSpeakerAnalysis(contentId)(storeState);
  const alignedRecordings = storeState.data[contentId]?.metadata?.alignedRecordings;

  const videoQuality = VIDEO_QUALITY.FULL_HD;
  const height = COMPOSITION_DIMENSIONS[clip.asset_metadata.size][videoQuality].HEIGHT;
  const width = COMPOSITION_DIMENSIONS[clip.asset_metadata.size][videoQuality].WIDTH;

  const fps = getClipFPS(clip);

  const showCaptions = !!clip.asset_metadata.subtitle || isClipAudiogram(clip);
  const segmentsTrackValue = clip.asset_metadata.player_config?.playerConfig.tracks.find(
    track => track.type === TrackType.SEGMENTS
  ) as SegmentsTrack;

  const segmentsTrack: SegmentsTrack = {
    id: MAIN_TRACK_ID,
    name: 'Segments Track',
    type: TrackType.SEGMENTS,
    hideTrack: {
      inTimeline: false,
      inPlayer: false
    },
    segments: [],
    segmentTransitions: segmentsTrackValue?.segmentTransitions || []
  };
  const existingCompositeSegment = segmentsTrackValue?.segments.find(
    segment => segment.type === SegmentType.COMPOSITE
  ) as CompositeSegment;

  let durationInFrames = 0;
  const introDuration = Math.floor((clip.asset_metadata.introDuration || 0) * fps);
  if (clip.asset_metadata.intro && introDuration) {
    segmentsTrack.segments.push(createIntroVideoSegment(clip, introDuration));
    durationInFrames += introDuration;
  }

  const compositeSegmentDuration = Math.floor((clip.asset_metadata?.duration || 0) * fps);
  let maxBounds = existingCompositeSegment?.maxBounds || {
    fromFrame: 0,
    toFrame: clip.asset_metadata.end * fps
  };

  if (isNotFullLengthClip(clip) && typeof clip.asset_metadata.maxVideoDuration === 'number') {
    maxBounds = {
      fromFrame: 0,
      toFrame: clip.asset_metadata.maxVideoDuration * fps
    };
  }

  if (
    typeof clip.asset_metadata.maxVideoDuration === 'number' &&
    clip.asset_metadata.maxVideoDuration > clip.asset_metadata.end &&
    isFullLengthClip(clip)
  ) {
    maxBounds = {
      fromFrame: 0,
      toFrame: clip.asset_metadata.maxVideoDuration * fps
    };
  }

  const compositeSegment = createMainCompositeSegment({
    clip,
    fps,
    duration: compositeSegmentDuration,
    offset: durationInFrames,
    sceneOverrides,
    facialRecognitionToDefaultPosition,
    videoFeeds,
    speakerAnalysis,
    alignedRecordings,
    isFullFaceRecEnabled,
    isFullFaceRecForPreRecordedEnabled,
    isScreenshareDetectionEnabled,
    isPlatformBrandKitEnabled,
    clipBrandKitStore,
    brandKitStore,
    platformBrandKit,
    organization
  });

  if (existingCompositeSegment?.effects) {
    compositeSegment.effects = existingCompositeSegment?.effects;
  }

  const playbackAdjustedEffects = mapEffectsToPlayableTimeline(
    compositeSegment.effects,
    compositeSegment.playRanges,
    fps
  );

  const playbackAdjustedScenes = mapScenesToPlayableTimeline(compositeSegment.scenes, compositeSegment.playRanges);

  segmentsTrack.segments.push({
    ...compositeSegment,
    playbackAdjustedEffects,
    playbackAdjustedScenes,
    maxBounds
  });
  durationInFrames += compositeSegmentDuration;

  const outroDuration = Math.floor((clip.asset_metadata.outroDuration || 0) * fps);
  if (clip.asset_metadata.outro && outroDuration) {
    segmentsTrack.segments.push(createOutroVideoSegment(clip, outroDuration, durationInFrames));
    durationInFrames += outroDuration;
  }

  let tracks: Track[] = [];
  if (segmentsTrack.segments.length) {
    const fontObject = getFontObject({
      clip,
      fontTarget: SelectionElementType.CAPTIONS,
      clipBrandKitStore,
      brandKitStore,
      platformBrandKit,
      organization,
      isPlatformBrandKitEnabled
    });
    const existingElementsTracks = getPlayerConfigElements(clip?.asset_metadata?.player_config);

    let updatedElementsTracks: ElementsTrack[] = [];
    if (existingElementsTracks) {
      updatedElementsTracks = updatePlayerConfigWithDeletes({
        playerConfig: existingElementsTracks,
        fps,
        prevClip: previousClip,
        newClip: clip,
        isSplitsTranslationEnabled,
        zoomLevel
      });
    }

    const metadata = buildCaptionMetadata({
      clip,
      isSplitsTranslationEnabled,
      clipBrandKitStore,
      brandKitStore,
      platformBrandKit,
      organization
    });

    tracks = [
      {
        segmentId: 'random',
        name: 'Captions',
        id: 'captions',
        type: TrackType.CAPTIONS,
        hideTrack: {
          inPlayer: !showCaptions,
          inTimeline: true
        },
        pages: [],
        metadata,
        fromFrame: compositeSegment.fromFrame,
        toFrame: compositeSegment.toFrame,
        ...fontObject
      }
    ];

    tracks.unshift(...updatedElementsTracks);
    tracks.unshift(segmentsTrack);
  } else {
    tracks = clip.asset_metadata.player_config?.playerConfig?.tracks || [];
  }

  return {
    height,
    width,
    fps,
    durationInFrames,
    version: '0.0.1',
    playerConfig: {
      tracks,
      metadata: getPlayerConfigMetadata(getClipReadOnlyPlayerConfig(clip))
    }
  };
}
